<template>
  <div class="meal-logger">
    <div class="logger-header">
      <h3>{{ isEditing ? 'Edit Meal' : 'Log Meal' }}</h3>
      <button v-if="showClose" class="close-btn" @click="$emit('close')">&times;</button>
    </div>

    <form @submit.prevent="handleSubmit">
      <!-- Meal Type -->
      <div class="form-group">
        <label>Meal Type</label>
        <div class="meal-type-grid">
          <button
            v-for="type in mealTypes"
            :key="type.value"
            type="button"
            class="meal-type-btn"
            :class="{ active: formData.meal_type === type.value }"
            @click="formData.meal_type = type.value"
          >
            <span class="type-symbol" :style="{ background: type.color }">{{ type.symbol }}</span>
            <span class="type-label">{{ type.label }}</span>
          </button>
        </div>
      </div>

      <!-- Description -->
      <div class="form-group">
        <label>What did you eat?</label>
        <textarea
          v-model="formData.description"
          placeholder="e.g., Grilled chicken with rice and vegetables"
          rows="2"
        ></textarea>
      </div>

      <!-- Quick Templates -->
      <div v-if="templates.length > 0 && !isEditing" class="quick-templates">
        <label>Quick Add</label>
        <div class="template-scroll">
          <button
            v-for="template in templates"
            :key="template.id"
            type="button"
            class="template-btn"
            @click="applyTemplate(template)"
          >
            <span class="template-name">{{ template.name }}</span>
            <span class="template-macros">
              C: {{ template.carbs }}g | P: {{ template.protein }}g | F: {{ template.fat }}g
            </span>
          </button>
        </div>
      </div>

      <!-- Macros -->
      <div class="form-group macros-section">
        <label>Nutrition (estimated)</label>
        <div class="macros-grid">
          <div class="macro-input">
            <span class="macro-label"><span class="macro-icon carbs">C</span> Carbs</span>
            <div class="input-with-unit">
              <input
                type="number"
                v-model.number="formData.carbs"
                placeholder="0"
                min="0"
                step="1"
              />
              <span class="unit">g</span>
            </div>
          </div>
          <div class="macro-input">
            <span class="macro-label"><span class="macro-icon protein">P</span> Protein</span>
            <div class="input-with-unit">
              <input
                type="number"
                v-model.number="formData.protein"
                placeholder="0"
                min="0"
                step="1"
              />
              <span class="unit">g</span>
            </div>
          </div>
          <div class="macro-input">
            <span class="macro-label"><span class="macro-icon fat">F</span> Fat</span>
            <div class="input-with-unit">
              <input
                type="number"
                v-model.number="formData.fat"
                placeholder="0"
                min="0"
                step="1"
              />
              <span class="unit">g</span>
            </div>
          </div>
        </div>

        <div class="calories-calculated">
          <span class="calc-label">Estimated Calories:</span>
          <span class="calc-value">{{ calculatedCalories }} kcal</span>
        </div>
      </div>

      <!-- Hydration -->
      <div class="form-group">
        <label>Fluids with this meal</label>
        <div class="hydration-row">
          <div class="input-with-unit hydration-input">
            <input
              type="number"
              v-model.number="formData.hydration_ml"
              placeholder="0"
              min="0"
              step="50"
            />
            <span class="unit">mL</span>
          </div>
          <div class="quick-hydration">
            <button
              v-for="portion in quickPortions"
              :key="portion.ml"
              type="button"
              class="hydration-btn"
              @click="formData.hydration_ml = portion.ml"
            >
              {{ portion.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Notes -->
      <div class="form-group">
        <label>Notes (optional)</label>
        <textarea
          v-model="formData.notes"
          placeholder="Any additional notes..."
          rows="1"
        ></textarea>
      </div>

      <!-- Actions -->
      <div class="form-actions">
        <button
          v-if="isEditing"
          type="button"
          class="delete-btn"
          @click="handleDelete"
          :disabled="saving"
        >
          Delete
        </button>
        <button
          type="button"
          class="cancel-btn"
          @click="$emit('close')"
          :disabled="saving"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="save-btn"
          :disabled="!isValid || saving"
        >
          {{ saving ? 'Saving...' : (isEditing ? 'Update' : 'Log Meal') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { Nutrition } from "@/services/nutrition-service";

export default {
  name: "MealLogger",

  props: {
    userId: {
      type: [Number, String],
      required: true,
    },
    mealDate: {
      type: String,
      default: () => new Date().toISOString().split("T")[0],
    },
    meal: {
      type: Object,
      default: null,
    },
    defaultMealType: {
      type: String,
      default: null,
    },
    templates: {
      type: Array,
      default: () => [],
    },
    showClose: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["saved", "deleted", "close"],

  data() {
    return {
      saving: false,
      formData: {
        meal_type: "breakfast",
        description: "",
        carbs: null,
        protein: null,
        fat: null,
        hydration_ml: 0,
        notes: "",
      },
    };
  },

  computed: {
    isEditing() {
      return !!this.meal?.id;
    },

    mealTypes() {
      return Nutrition.getAllMealTypes();
    },

    quickPortions() {
      return Nutrition.getQuickPortions();
    },

    calculatedCalories() {
      return Nutrition.calculateCalories(
        this.formData.carbs,
        this.formData.protein,
        this.formData.fat
      );
    },

    isValid() {
      return (
        this.formData.meal_type &&
        (this.formData.description ||
          this.formData.carbs > 0 ||
          this.formData.protein > 0 ||
          this.formData.fat > 0)
      );
    },
  },

  watch: {
    meal: {
      immediate: true,
      handler(meal) {
        if (meal) {
          this.formData = {
            meal_type: meal.meal_type || "breakfast",
            description: meal.description || "",
            carbs: meal.carbs || null,
            protein: meal.protein || null,
            fat: meal.fat || null,
            hydration_ml: meal.hydration_ml || 0,
            notes: meal.notes || "",
          };
        } else {
          this.resetForm();
        }
      },
    },

    defaultMealType: {
      immediate: true,
      handler(type) {
        if (type && !this.isEditing) {
          this.formData.meal_type = type;
        }
      },
    },
  },

  methods: {
    resetForm() {
      this.formData = {
        meal_type: this.defaultMealType || "breakfast",
        description: "",
        carbs: null,
        protein: null,
        fat: null,
        hydration_ml: 0,
        notes: "",
      };
    },

    applyTemplate(template) {
      this.formData.description = template.name;
      this.formData.carbs = template.carbs;
      this.formData.protein = template.protein;
      this.formData.fat = template.fat;
    },

    async handleSubmit() {
      if (!this.isValid || this.saving) return;

      this.saving = true;
      try {
        const mealData = {
          user_id: this.userId,
          meal_date: this.mealDate,
          meal_type: this.formData.meal_type,
          description: this.formData.description,
          carbs: this.formData.carbs || 0,
          protein: this.formData.protein || 0,
          fat: this.formData.fat || 0,
          calories: this.calculatedCalories,
          hydration_ml: this.formData.hydration_ml || 0,
          notes: this.formData.notes,
        };

        let response;
        if (this.isEditing) {
          response = await Nutrition.updateMeal(this.meal.id, mealData);
        } else {
          response = await Nutrition.logMeal(mealData);
        }

        if (response.data.success) {
          this.$toast.success(this.isEditing ? "Meal updated" : "Meal logged");
          this.$emit("saved", response.data);
          if (!this.isEditing) {
            this.resetForm();
          }
        } else {
          this.$toast.error(response.data.error || "Failed to save meal");
        }
      } catch (error) {
        console.error("Error saving meal:", error);
        this.$toast.error("Failed to save meal");
      } finally {
        this.saving = false;
      }
    },

    async handleDelete() {
      if (!this.isEditing || this.saving) return;

      if (!confirm("Delete this meal?")) return;

      this.saving = true;
      try {
        const response = await Nutrition.deleteMeal(this.meal.id, this.userId);

        if (response.data.success) {
          this.$toast.success("Meal deleted");
          this.$emit("deleted", this.meal.id);
        } else {
          this.$toast.error(response.data.error || "Failed to delete meal");
        }
      } catch (error) {
        console.error("Error deleting meal:", error);
        this.$toast.error("Failed to delete meal");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.meal-logger {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 100%;
}

.logger-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    color: #333;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #666;
    cursor: pointer;
    padding: 0;
    line-height: 1;

    &:hover {
      color: #333;
    }
  }
}

form {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;

  > label {
    display: block;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
    font-size: 0.9rem;
  }

  textarea,
  input[type="text"] {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    resize: vertical;

    &:focus {
      outline: none;
      border-color: #007bff;
    }
  }
}

.meal-type-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.meal-type-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 8px;
  background: #f8f9fa;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e9ecef;
  }

  &.active {
    background: #e7f1ff;
    border-color: #007bff;
  }

  .type-symbol {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: 700;
    color: white;
  }

  .type-label {
    font-size: 0.7rem;
    color: #666;
    text-align: center;
  }
}

.quick-templates {
  margin-bottom: 20px;

  label {
    display: block;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
    font-size: 0.9rem;
  }
}

.template-scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 8px;
  -webkit-overflow-scrolling: touch;
}

.template-btn {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #e9ecef;
    border-color: #007bff;
  }

  .template-name {
    font-weight: 600;
    font-size: 0.85rem;
    color: #333;
  }

  .template-macros {
    font-size: 0.7rem;
    color: #666;
  }
}

.macros-section {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.macros-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.macro-input {
  display: flex;
  flex-direction: column;
  gap: 6px;

  .macro-label {
    font-size: 0.8rem;
    color: #666;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .macro-icon {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: 700;
    color: white;

    &.carbs {
      background: linear-gradient(135deg, #ffc107, #ff9800);
    }

    &.protein {
      background: linear-gradient(135deg, #dc3545, #c82333);
    }

    &.fat {
      background: linear-gradient(135deg, #28a745, #20c997);
    }
  }
}

.input-with-unit {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;

  input {
    flex: 1;
    border: none;
    padding: 8px 10px;
    font-size: 1rem;
    min-width: 0;

    &:focus {
      outline: none;
    }
  }

  .unit {
    padding: 8px 10px;
    background: #e9ecef;
    color: #666;
    font-size: 0.85rem;
  }
}

.calories-calculated {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #ddd;

  .calc-label {
    color: #666;
    font-size: 0.85rem;
  }

  .calc-value {
    font-weight: 700;
    color: #333;
  }
}

.hydration-row {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.hydration-input {
  width: 120px;
}

.quick-hydration {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.hydration-btn {
  padding: 6px 12px;
  background: #e7f1ff;
  border: 1px solid #007bff;
  border-radius: 16px;
  color: #007bff;
  font-size: 0.75rem;
  cursor: pointer;

  &:hover {
    background: #007bff;
    color: white;
  }
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #eee;

  button {
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .cancel-btn {
    background: transparent;
    border: 1px solid #666;
    color: #666;

    &:hover:not(:disabled) {
      background: #f0f0f0;
    }
  }

  .save-btn {
    background: #28a745;
    border: none;
    color: white;

    &:hover:not(:disabled) {
      background: #1e7e34;
    }
  }

  .delete-btn {
    background: transparent;
    border: 1px solid #dc3545;
    color: #dc3545;
    margin-right: auto;

    &:hover:not(:disabled) {
      background: #dc3545;
      color: white;
    }
  }
}

/* Mobile adjustments */
@media screen and (max-width: 480px) {
  .logger-header {
    padding: 12px 16px;
  }

  form {
    padding: 16px;
  }

  .meal-type-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
  }

  .meal-type-btn {
    padding: 8px 4px;

    .type-emoji {
      font-size: 1rem;
    }

    .type-label {
      font-size: 0.6rem;
    }
  }

  .macros-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-wrap: wrap;

    button {
      flex: 1;
      min-width: 100px;
    }

    .delete-btn {
      width: 100%;
      margin-right: 0;
      order: 3;
    }
  }
}
</style>
